import { GhostSuggestion } from '../GhostMoveCard';
import type { RescheduleEntry } from '../ServiceFailureBadge';
import type { RouteEtaData } from './RouteEta';

export type { GhostSuggestion, RescheduleEntry };

export interface DisplaySettings {
  statusTypes: {
    notStarted: boolean;
    betweenMoves: boolean;
    currentMove: boolean;
  };
  cardOptions: {
    showContainerNumber: boolean;
    showSize: boolean;
    showChassis: boolean;
    showAccountStatus: boolean;
    compactView: boolean;
    superCompactView: boolean;
    superCompactFontSize?: number;
  };
  other: {
    endorsements: boolean;
    ghostCardCount?: number;
  };
}

export const DEFAULT_DISPLAY_SETTINGS: DisplaySettings = {
  statusTypes: {
    notStarted: true,
    betweenMoves: false,
    currentMove: false,
  },
  cardOptions: {
    showContainerNumber: false,
    showSize: false,
    showChassis: false,
    showAccountStatus: true,
    compactView: false,
    superCompactView: false,
  },
  other: {
    endorsements: false,
    ghostCardCount: 1,
  },
};

export interface TroubleTicketEntry {
  recno: number;
  datetime: string;
  category: string;
  status: string;
}

export interface MoveRisk {
  live_share_url?: string;
  live_share_expires_at?: string;
  route_eta?: RouteEtaData | null;
  pronumber: number;
  legnum: number;
  movetype: string;
  driver: string;
  dispatcher: string;
  region: string;
  billto_name: string;
  customer_name: string;
  origin_name: string;
  origin_city_state: string;
  dest_name: string;
  dest_city_state: string;
  dest_timezone: string;
  dest_tz_identifier?: string;
  origcode: number;
  destcode: number;
  origin_lat: number | null;
  origin_lng: number | null;
  dest_lat: number | null;
  dest_lng: number | null;
  appointment_time: string;
  deadline_date: string;
  deadline_date_raw: string;
  deadline_time: string;
  deadline_type: string | null;
  per_diem_date: string | null;
  cutoff_date: string | null;
  cutoff_time?: string | null;
  container: string;
  size: string;
  equiptype: string;
  chassisid?: string | null;
  chassistype?: string | null;
  legdrop: string;
  legaction2: string;
  status: string;
  estimated_minutes: number;
  route_duration_minutes: number;
  time_remaining_minutes: number | null;
  risk_score: number;
  risk_level: 'critical' | 'high' | 'medium' | 'low' | 'arrived' | 'unknown';
  risk_factor: 'recovery_time' | null;
  driver_lat: number | null;
  driver_lng: number | null;
  distance_to_dest_miles: number | null;
  driver_speed_mph: number | null;
  has_realtime_location: boolean;
  can_reassign: boolean;
  requires_hazmat: boolean;
  requires_tank: boolean;
  requires_93k: boolean;
  requires_daycab: boolean;
  completion_order?: number;
  travel_to_next_minutes?: number | null;
  dest_arrival_time?: string;
  dest_depart_time?: string;
  dispatch_priority?: number | null;
  sitting_minutes?: number | null;
  origapptdate?: string;
  destapptdate?: string;
  eta_based_on_geofence?: boolean;
  accept_reject?: string;
  key_account?: string;
  orig_is_new_location?: string;
  dest_is_new_location?: string;
  deliveryleg?: number;
  trouble_ticket_count?: number;
  trouble_tickets?: TroubleTicketEntry[];
  /** Delivery reschedule count (ordersall.deliveryrescheduled). > 0 = red card border. */
  reschedule_count?: number;
  /** Reschedule detail behind the SF badge tooltip (shared with the At Risk Monitor). */
  reschedule_history?: RescheduleEntry[];
  legtype?: string;
  lfd?: string | null;
  storage_cleared_through?: string | null;
  matches_dispatcher_filter?: boolean;
  matches_region_filter?: boolean;
  appt_missed?: boolean;
  dispatch_sent_at?: string | null;
}

export interface DriverWithMoves {
  isMule?: boolean;
  unitnumber: string;
  driver_name: string;
  driver_dispatcher: string;
  region: string | null;
  has_hazmat: boolean;
  has_tank: boolean;
  has_93k: boolean;
  is_daycab: boolean;
  is_light_truck: boolean;
  driver_lat: number | null;
  driver_lng: number | null;
  driver_location_time: string | null;
  samsara_vehicle_id: string | null;
  samsara_driver_id: string | null;
  textmsgnumber: string | null;
  homephone: string | null;
  preference: string | null;
  truck_license_plate: string | null;
  time_utilization_pct: number;
  hos_utilization_pct: number;
  limiting_factor: 'time' | 'hos';
  drive_time_remaining_mins: number | null;
  cycle_time_remaining_mins: number | null;
  break_time_remaining_mins: number | null;
  shift_time_remaining_mins: number | null;
  time_in_current_status: string | null;
  total_drive_time_needed_mins: number;
  missing_hos: boolean;
  stale_gps: boolean;
  vin_mismatch_issue: string | null;
  out_of_hours: boolean;
  off_duty: boolean;
  out_of_service?: boolean;
  out_of_service_reason?: string | null;
  out_of_service_date?: string | null;
  current_duty_status: string | null;
  /** First on-duty time for the viewed day from the Samsara HOS duty-day rollup.
   *  Central wall-clock ISO with no zone (e.g. "2026-10-15T06:15:02") — format
   *  it as-is, never through Date. */
  driver_start_at?: string | null;
  /** Shift was already running at midnight; driver_start_at is the PRIOR day's start. */
  driver_start_carried_over?: boolean;
  risk_level: 'critical' | 'at_risk' | 'on_track';
  today_comment: string | null;
  total_moves: number;
  moves: MoveRisk[];
  ghost_suggestions?: GhostSuggestion[];
  // Populated only on lanes returned from /dispatch-requests for the
  // Requesting Work section.
  formatted_location?: string | null;
  request_createdatetime?: string | null;
  request_recno?: number | null;
}

export interface DriverRiskData {
  critical_drivers: DriverWithMoves[];
  at_risk_drivers: DriverWithMoves[];
  on_track_drivers: DriverWithMoves[];
  completed_drivers: DriverWithMoves[];
  total_drivers: number;
  total_moves: number;
  timestamp: string;
  is_today: boolean;
  start_date: string;
  end_date: string;
}

export interface MoveGroup {
  id: string;
  moves: MoveRisk[];
  isGroup: boolean;
}

export interface DragState {
  move: MoveRisk;
  sourceDriver: string;
  group: MoveGroup | null;
}

export type ProjectedStatus = 'on_track' | 'at_risk' | 'will_miss' | 'missed';
